const GROQ_API = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_MODEL = 'llama-3.3-70b-versatile';
const STORAGE_KEY_GROQ = 'BUILDPRO_GROQ_KEY';

// ── Key Management ──────────────────────────────────────────────────
export const getGroqKey = (): string | null => localStorage.getItem(STORAGE_KEY_GROQ) || import.meta.env.VITE_GROQ_API_KEY || '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';
export const setGroqKey = (key: string) => {
  if (!key) localStorage.removeItem(STORAGE_KEY_GROQ);
  else localStorage.setItem(STORAGE_KEY_GROQ, key);
};

export const getLocalGroqKey = getGroqKey;
export const setLocalGroqKey = setGroqKey;

// ── Core Call (BuildPro Legacy) ──────────────────────────────────────
async function callGroq(
  systemPrompt: string,
  userMessage: string,
  maxTokens: number = 2048
): Promise<string> {
  const apiKey = getGroqKey();
  if (!apiKey) throw new Error('No se ha configurado la API key de Groq. Es gratuita: console.groq.com');

  const res = await fetch(GROQ_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage },
      ],
      max_tokens: maxTokens,
      temperature: 0.3,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Groq: ${err?.error?.message || `Error ${res.status}`}`);
  }

  const data = await res.json();
  return data.choices?.[0]?.message?.content || '';
}

// ── ContikPro OCR and Text Functions ────────────────────────────────
export async function callGroqOCR(
  systemPrompt: string,
  userMessage: string,
  base64Image?: string,
  maxTokens: number = 1024
): Promise<string> {
  const apiKey = getLocalGroqKey();
  if (!apiKey) throw new Error('No se ha configurado la API key de Groq.');

  const model = base64Image ? 'llama-3.2-90b-vision-preview' : 'llama-3.3-70b-versatile';

  const messages: any[] = [
    { role: 'system', content: systemPrompt }
  ];

  if (base64Image) {
      messages.push({
          role: 'user',
          content: [
              { type: 'text', text: userMessage },
              {
                  type: 'image_url',
                  image_url: {
                      url: `data:image/jpeg;base64,${base64Image}`
                  }
              }
          ]
      });
  } else {
      messages.push({ role: 'user', content: userMessage });
  }

  const res = await fetch(GROQ_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages,
      max_tokens: maxTokens,
      temperature: 0.1, // Low temperature for JSON extraction
      response_format: { type: "json_object" } // Force JSON output if supported
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Groq: ${err?.error?.message || `Error ${res.status}`}`);
  }

  const data = await res.json();
  const content = data.choices?.[0]?.message?.content || '{}';
  return content.replace(/```json/g, '').replace(/```/g, '').trim();
}

export async function callGroqText(
  systemPrompt: string,
  userMessage: string,
  maxTokens: number = 512
): Promise<string> {
  return callGroq(systemPrompt, userMessage, maxTokens);
}

// ── BuildPro Smart Features ──────────────────────────────────────────────────

export async function explainBudgetItem(itemName: string, unit: string, price: number): Promise<string> {
  return callGroq('Eres un aparejador español...', `Explica qué es "${itemName}"...`, 300);
}

export async function suggestAlternatives(itemName: string, currentPrice: number, unit: string): Promise<string> {
  return callGroq('Eres un aparejador experto...', `La partida "${itemName}"...`, 800);
}

export async function generateMemoria(mode: string, tipoObra: string, superficie: number, items: any[], totalExVat: number): Promise<string> {
  return callGroq('Eres un Arquitecto Técnico...', `Redacta una Memoria...`, 1500);
}

export async function askConstructionQuestion(question: string): Promise<string> {
  return callGroq('Eres un experto en construcción y reformas...', question, 1000);
}

export async function analyzeBudgetSavings(items: any[], totalExVat: number): Promise<string> {
  return callGroq('Eres un consultor de costes...', `Analiza este presupuesto...`, 1500);
}

export async function askProjectContextQuestion(
  context: { projectName: string; budget: number; tasksInfo: string; },
  question: string,
  history: Array<{ role: 'user' | 'assistant' | 'system'; content: string }> = []
): Promise<string> {
  const systemPrompt = `Eres un asistente inteligente para la gestión de proyectos de construcción de Tonveon BuildPro. 
Resuelves dudas sobre el proyecto activo. Hablas en español, eres profesional, directo y amable.
Contexto del Proyecto actual:
- Nombre: ${context.projectName}
- Presupuesto estimado: ${context.budget.toLocaleString('es-ES')}€
- Tareas: ${context.tasksInfo || 'Sin tareas registradas todavía'}
Tu objetivo es responder cualquier duda basándote en esta información y en tu conocimiento general de arquitectura y construcción en España.`;

  const apiKey = getGroqKey();
  if (!apiKey) throw new Error('No se ha configurado la API key de Groq. Es gratuita: console.groq.com');

  const res = await fetch(GROQ_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      messages: [
        { role: 'system', content: systemPrompt },
        ...history,
        { role: 'user', content: question },
      ],
      max_tokens: 1500,
      temperature: 0.3,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Groq: ${err?.error?.message || `Error ${res.status}`}`);
  }

  const data = await res.json();
  return data.choices?.[0]?.message?.content || '';
}
